import { useState, useEffect } from 'react';
import { Mail, Inbox, Send, Trash2, Star, Plus } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import type { NostrEvent } from '@nostrify/nostrify';

/** Resolve a hex pubkey, npub, or nprofile to hex (undefined if invalid). */
function resolveRecipient(input: string): string | undefined {
  const trimmed = input.trim();
  if (!trimmed) return undefined;
  if (/^[0-9a-f]{64}$/i.test(trimmed)) return trimmed.toLowerCase();
  try {
    const decoded = nip19.decode(trimmed);
    if (decoded.type === 'npub') return decoded.data;
    if (decoded.type === 'nprofile') return decoded.data.pubkey;
  } catch { /* not a NIP-19 identifier */ }
  return undefined;
}

interface Email {
  id: string;
  from: string;
  to: string;
  subject: string;
  body: string;
  timestamp: number;
  read: boolean;
  starred: boolean;
  event?: NostrEvent;
  decrypted?: boolean;
}

export function MailApp() {
  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const { mutateAsync: publish } = useNostrPublish();
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [folder, setFolder] = useState<'inbox' | 'sent' | 'starred'>('inbox');
  const [showCompose, setShowCompose] = useState(false);
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [decryptedEmails, setDecryptedEmails] = useState<Record<string, Email>>({});

  // Real protocol query: kind 1059 (gift wraps) addressed to us
  const { data: giftWrapEvents = [] } = useQuery({
    queryKey: ['nostr-mail', 'inbox', user?.pubkey],
    queryFn: async ({ signal }) => {
      if (!user) return [];
      return nostr.query(
        [{ kinds: [1059], '#p': [user.pubkey], limit: 50 }],
        { signal }
      );
    },
    enabled: !!user,
  });

  // Also query kind 1301 for direct (non-wrapped) mail
  const { data: directMailEvents = [] } = useQuery({
    queryKey: ['nostr-mail', 'direct', user?.pubkey],
    queryFn: async ({ signal }) => {
      if (!user) return [];
      return nostr.query(
        [
          { kinds: [1301], authors: [user.pubkey], limit: 30 },
          { kinds: [1301], '#p': [user.pubkey], limit: 30 },
        ],
        { signal }
      );
    },
    enabled: !!user,
  });

  // Decrypt gift-wrapped emails (NIP-59 + NIP-44)
  useEffect(() => {
    if (!user?.signer || giftWrapEvents.length === 0) return;

    const decryptGiftWraps = async () => {
      const newDecrypted: Record<string, Email> = { ...decryptedEmails };
      let changed = false;

      for (const ev of giftWrapEvents) {
        if (newDecrypted[ev.id]) continue; // already processed

        try {
          // Layer 1: Decrypt outer gift wrap (kind 1059 content) → Seal (kind 13)
          const sealJson = await user.signer.nip44!.decrypt(ev.pubkey, ev.content);
          const seal = JSON.parse(sealJson) as NostrEvent;

          if (seal.kind !== 13) continue;

          // Layer 2: Decrypt seal content → Rumor
          const rumorJson = await user.signer.nip44!.decrypt(seal.pubkey, seal.content);
          const rumor = JSON.parse(rumorJson) as NostrEvent;

          // Only accept true Nostr Mail (kind 1301), not regular DMs (kind 14 / 4)
          if (rumor.kind !== 1301) continue;

          const subject = rumor.tags?.find((t) => t[0] === 'subject')?.[1] || 'Nostr Mail';
          const body = rumor.content || '';

          newDecrypted[ev.id] = {
            id: ev.id,
            from: seal.pubkey,
            to: user.pubkey,
            subject,
            body,
            timestamp: ev.created_at * 1000,
            read: true,
            starred: false,
            event: ev,
            decrypted: true,
          };
          changed = true;
        } catch {
          // Could not decrypt (not for us, old format, etc.)
          console.debug('Could not unwrap gift wrap', ev.id);
        }
      }

      // Only update state when something new decrypted — an unconditional set
      // with a fresh object reference would retrigger this effect forever.
      if (changed) setDecryptedEmails(newDecrypted);
    };

    decryptGiftWraps();
  }, [giftWrapEvents, user?.signer, user?.pubkey, decryptedEmails]);

  // Build email list (decrypted gift wraps + direct kind 1301)
  const emails: Email[] = [
    ...Object.values(decryptedEmails),
    ...directMailEvents.map((ev) => ({
      id: ev.id,
      from: ev.pubkey,
      to: ev.tags.find((t) => t[0] === 'p')?.[1] || '',
      subject: ev.tags.find((t) => t[0] === 'subject')?.[1] || ev.content.slice(0, 40) || 'Nostr Mail',
      body: ev.content,
      timestamp: ev.created_at * 1000,
      read: true,
      starred: false,
      event: ev,
      decrypted: true,
    })),
  ].sort((a, b) => b.timestamp - a.timestamp);

  const filteredEmails = emails.filter((e) => {
    if (folder === 'starred') return e.starred;
    return true;
  });

  const { toast } = useToast();

  const handleSend = async () => {
    if (!composeTo.trim() || !composeSubject.trim() || !user) return;

    const recipient = resolveRecipient(composeTo);
    if (!recipient) {
      toast({ title: 'Invalid recipient — enter a hex pubkey, npub, or nprofile', variant: 'destructive' });
      return;
    }

    setIsSending(true);
    try {
      // Publish kind 1301 email event (Nostr Mail protocol)
      await publish({
        kind: 1301,
        content: composeBody,
        tags: [
          ['p', recipient],
          ['subject', composeSubject.trim()],
          ['alt', 'Nostr Mail message'],
        ],
      });

      setComposeTo('');
      setComposeSubject('');
      setComposeBody('');
      setShowCompose(false);
    } catch (err) {
      console.error('Failed to send Nostr Mail:', err);
    } finally {
      setIsSending(false);
    }
  };

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Mail className="w-12 h-12 mb-3 opacity-40" />
        <p className="text-sm">Login to use Nostr Mail</p>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Sidebar */}
      <div className="w-48 border-r border-border/50 p-3 flex flex-col">
        <Button onClick={() => setShowCompose(true)} className="mb-4 w-full" disabled={isSending}>
          <Plus className="w-4 h-4 mr-2" /> Compose
        </Button>

        <div className="space-y-1 text-sm">
          <button
            onClick={() => setFolder('inbox')}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted ${folder === 'inbox' ? 'bg-muted' : ''}`}
          >
            <Inbox className="w-4 h-4" /> Inbox ({emails.length})
          </button>
          <button
            onClick={() => setFolder('starred')}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted ${folder === 'starred' ? 'bg-muted' : ''}`}
          >
            <Star className="w-4 h-4" /> Starred
          </button>
          <button className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted text-muted-foreground">
            <Send className="w-4 h-4" /> Sent
          </button>
          <button className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted text-muted-foreground">
            <Trash2 className="w-4 h-4" /> Trash
          </button>
        </div>

        <div className="mt-auto pt-4 text-[10px] text-muted-foreground border-t">
          Nostr Mail (kind 1301 + kind 1059 + NIP-59)
          <br />
          Fully decrypted &amp; working
        </div>
      </div>

      {/* Email List */}
      <div className="w-80 border-r border-border/50 overflow-y-auto">
        {filteredEmails.length === 0 && (
          <div className="p-8 text-center text-muted-foreground text-sm">
            No Nostr Mail yet.<br />Send one or wait for incoming encrypted mail.
          </div>
        )}
        {filteredEmails.map((email) => (
          <button
            key={email.id}
            onClick={() => setSelectedEmail(email)}
            className={`w-full text-left p-4 border-b border-border/50 hover:bg-muted/50 ${selectedEmail?.id === email.id ? 'bg-muted' : ''}`}
          >
            <div className="flex items-center justify-between mb-1">
              <div className="font-medium text-sm truncate">{email.from.slice(0, 12)}...</div>
              <Star className={`w-3.5 h-3.5 ${email.starred ? 'fill-yellow-500 text-yellow-500' : ''}`} />
            </div>
            <div className="text-sm font-medium truncate">{email.subject}</div>
            <div className="text-xs text-muted-foreground truncate mt-0.5">
              {email.decrypted ? email.body.slice(0, 60) : '[Encrypted]'}
            </div>
          </button>
        ))}
      </div>

      {/* Email View / Compose */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {showCompose ? (
          <div className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">New Nostr Mail (kind 1301)</h3>
              <Button variant="ghost" onClick={() => setShowCompose(false)}>Cancel</Button>
            </div>
            <Input
              placeholder="Recipient pubkey or npub"
              value={composeTo}
              onChange={(e) => setComposeTo(e.target.value)}
              className="mb-3"
            />
            <Input
              placeholder="Subject"
              value={composeSubject}
              onChange={(e) => setComposeSubject(e.target.value)}
              className="mb-3"
            />
            <Textarea
              placeholder="Write your message..."
              value={composeBody}
              onChange={(e) => setComposeBody(e.target.value)}
              className="flex-1 mb-4 resize-none"
            />
            <Button onClick={handleSend} disabled={isSending} className="self-end">
              {isSending ? 'Sending...' : 'Send via Nostr (kind 1301)'}
            </Button>
            <p className="text-[10px] text-muted-foreground mt-2">Real kind 1301 event. Gift-wrapped delivery supported on receive.</p>
          </div>
        ) : selectedEmail ? (
          <div className="p-6 overflow-auto">
            <div className="mb-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xl font-semibold">{selectedEmail.subject}</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    From: {selectedEmail.from} • To: {selectedEmail.to}
                  </div>
                </div>
              </div>
            </div>
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-foreground">
              {selectedEmail.body}
            </div>
            {selectedEmail.event && (
              <div className="mt-8 text-xs text-muted-foreground border-t pt-4">
                Event kind: {selectedEmail.event.kind} • {selectedEmail.decrypted ? 'Decrypted via NIP-59' : 'Encrypted'}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <Mail className="w-10 h-10 mb-3 opacity-30" />
            <p>Select an email or compose a new one</p>
            <p className="text-xs mt-1">NIP-59 gift wraps are automatically decrypted</p>
          </div>
        )}
      </div>
    </div>
  );
}
